import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface FormDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
  /** Defaults to "Cancel"; pass a translated label on localized surfaces. */
  cancelLabel?: string;
  submitColor?: 'primary' | 'error' | 'warning';
  submitDisabled?: boolean;
  /** Shows a spinner on the submit button and locks both actions while the submit runs. */
  loading?: boolean;
  /** Hide Cancel when the dialog is a read-only view with a single dismiss action. */
  hideCancel?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Allow focus to leave the dialog (needed for Autocomplete menus portaled to the body). */
  disableEnforceFocus?: boolean;
  children: React.ReactNode;
}

export const FormDialog: React.FC<FormDialogProps> = ({
  open,
  title,
  onClose,
  onSubmit,
  submitLabel,
  cancelLabel = 'Cancel',
  submitColor = 'primary',
  submitDisabled = false,
  loading = false,
  hideCancel = false,
  maxWidth = 'sm',
  disableEnforceFocus = false,
  children,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      disableEnforceFocus={disableEnforceFocus}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {!hideCancel && (
          <Button onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
        )}
        <Button
          onClick={onSubmit}
          variant="contained"
          color={submitColor}
          disabled={submitDisabled || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          aria-busy={loading || undefined}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

